import Mosaic from 'mosaic-framework';

import '../components/navigation-bar';
import '../components/footer';
import '../components/round-button';
import '../components/code-segment';

import '../styles/docs.less';


export default new Mosaic({
    name: 'view-page',
    created() {
        window.scrollTo({ top: 0 });

        const menubtn = document.getElementById('menu-button');
        menubtn.style.display = 'block';

        if(menubtn.onclick) return;

        menubtn.onclick = () => {
            const menu = document.getElementById('doc-menu');
            menu.style.transform = 'translateX(0%)';
        }
    },
    view() {
        return html`<div class='doc-page'>
            <navigation-bar></navigation-bar>
            
            <h1 class='section-title' tabindex='0'>🏞 Views</h1>
            <p class='section-text' tabindex='0'>
                Finally we get to the exciting part: <b>views</b>!
            </p>
            <p class='section-text' tabindex='0'>
                Views are used to tell Mosaic what you want your component to look like
                once they are rendered onto the page. A component's "view" is simply a
                function that returns some html. By returning html, I literally mean "html."
                Mosaic defines a global function on JavaScript's "window" object called 
                "html" which is meant to be used as a <b>Tagged Template Literal</b>. All
                Mosaic view functions <u>must</u> return this html function to work
                properly. Returning any other value results in undefined behavior.
            </p>

            <h1 class='section-title' tabindex='0'>Tagged Template Literals</h1>
            <p class='section-text' tabindex='0'>
                Tagged Template Literals (further abbreviated as TTLs) are a built-in feature
                of the JavaScript langauge and they allow us to use the backtick strings along
                with a function to separate our static content from our dynamic content. All
                you have to do is write "html" and then directly after it, with no function
                parentheses, add the backtick strings like so:
            </p>
            <code-segment type="JS">
                ${"const emoji = \":D\";"}
                ${html`<br>`}
                ${"html`<div>Here is a Tagged Template Literal ${emoji}</div>`"}
                <span style='color:green'>/* Here is a Tagged Template Literal :D */</span>
            </code-segment>
            <p class='section-text' tabindex='0'>
                By using TTLs, we can write regular HTML without the need for fillers like
                the camelCased "onClick" event or "style" being an object. Instead, we use
                simple HTML like we are all used to like "onclick" and "style='color:green'".
                TTLs also allow us to write adjacent HTML tags without having to wrap them
                in a &lt;div&gt; tag. The only exception to this is conditionally rendered
                html functions and html functions used inside of arrays.
            </p>
            
            <h1 class='section-title' tabindex='0'>Dynamic Content</h1>
            <p class='section-text' tabindex='0'>
                Component views will almost always contain dynamic content. To achieve this
                using TTLs you will have to use the <b>"\${ ...expression... }"</b> syntax 
                within the html function. The "dollar sign brackets" will wrap any JavaScript
                expression so that it can be presented at the end of the repaint cycle. You
                can include these brackets in attributes as well as for nodes. But what kinds
                of data can you include inside these brackets? As of now, the supported data
                types are strings, numbers, booleans, big ints, functions, arrays, objects, 
                and the <b>html</b> function.
            </p>
            <p class='section-text' tabindex='0'>
                There is, however, an important caveat to using dynamic descendants that comes
                into play when rendering arrays. This will be explained more below under the
                section titled "Array Caveats."
            </p>
            <code-segment type='JS'>
                ${"const age = 21;"}
                ${html`<br>`}
                ${"html`<div>I am ${age} years old!</div>`"}&nbsp;
                <span style='color:green'>/* I am 21 years old! */</span>
            </code-segment>
            <br>

            <h1 class='section-title' tabindex='0'>Composing Components</h1>
            <p class='section-text' tabindex='0'>
                Mosaic allows you to compose parent and child components in order to create
                complex user interfaces. To do this, we take advantage of the fact that the
                html function can be rendered using dynamic content brackets as was mentioned
                above. All you have to do is add the "dollar sign brackets" and then write a
                new html function inside of it. Given that each Mosaic component has a name
                attached to it, we can just use its name as a custom HTML tag and see it
                rendered within our parent view! Take a look at the following example to see
                how this is done:
            </p>
            <iframe width="100%" height="300" src="//jsfiddle.net/y0pnq6z3/embedded/js,html,result/" allowfullscreen="allowfullscreen" allowpaymentrequest frameborder="0"></iframe>
            <p class='section-text' tabindex='0'>
                You'll notice that the first component uses an arrow function for its view.
                Normally, this would not be ok because arrow functions do not have a reference
                to "this" to refer to the current component instance. To solve this, the view
                function passes along a reference to itself as a parameter, so you can call it
                something like "self" and still have access to the entire component.
            </p>

            <h1 class='section-title' tabindex='0'>Conditional Rendering</h1>
            <p class='section-text' tabindex='0'>
                Another topic to discuss in relation to component views is that of conditional
                rendering. Remember how we said that views are memorized upon initialization so
                that we don't need to traverse them again later on? Well, this has the side
                effect of only memorizing the view as a function of the component state at the 
                time of creation, meaning that you cannot directly write an if-statement in your
                view to return different html functions.
            </p>
            <b style='color:crimson'>
                <ion-icon name="close"></ion-icon>
                &nbsp;Bad: This view will always show "Off"
            </b>
            <iframe width="100%" height="300" src="//jsfiddle.net/authman2/3rn4hbzu/1/embedded/js,result/" allowfullscreen="allowfullscreen" allowpaymentrequest frameborder="0"></iframe>
            <p class='section-text' tabindex='0'>
                The proper way to conditionally render content is to wrap it in a function and
                then use the "dollar sign bracket" syntax to add it to your view. The Mosaic
                renderer will see that it is a function and subsequently render it at the time
                of the <i>update</i> rather than the time of <i>initialization</i>. Here is a working
                example:
            </p>
            <b style='color:mediumseagreen'>
                <ion-icon name="checkmark"></ion-icon>
                &nbsp;Good: "On" and "Off" will now toggle
            </b>
            <iframe style='margin-bottom:10px' width="100%" height="300" src="//jsfiddle.net/authman2/3rn4hbzu/4/embedded/js,result/" allowfullscreen="allowfullscreen" allowpaymentrequest frameborder="0"></iframe>
            <br>

            <h1 class='section-title' tabindex='0'>Rendering Arrays</h1>
            <p class='section-text' tabindex='0'>
                How can we include lists of data in our views? Well, there are two ways.
                The first is to directly map our list items to the html function like so:
            </p>
            <code-segment>
                ${html`this.data.items.map(item => `}
                ${"html`<h1>${item}</h1>`"}
                ${");"}
            </code-segment>
            <p class='section-text' tabindex='0'>
                This is the simplest way to render an array, however it involves wrapping
                you list inside a div and recreating the entire list on every update. The
                second way to render arrays is much more performant, and uses keys to make
                efficient updates to your list. It also does not require your list to be
                wrapped in a container div.
            </p>

            <h1 class='section-title' tabindex='0'>
                Mosaic.list(items: <span style='color:mediumseagreen'>Array</span>,
                keyFunction: <span style='color:mediumseagreen'>Function</span>,
                mapFunction: <span style='color:mediumseagreen'>Function</span>)
            </h1>
            <p class='section-text' tabindex='0'>
                We use the static <b>Mosaic.list</b> function to give unique keys to our
                list elements so that they can be quickly looked up later on to make changes.
                The first parameter is the list of items to render. The second is a function
                that maps a single list item to a unique key string. And the third parameter
                is a function that maps the array item to an html function that can be
                rendered on the screen.
            </p>
            <code-segment>
                ${html`Mosaic.list(this.data.items, (item, index) => {`}
                ${html`<br>`}
                ${html`&nbsp;&nbsp;&nbsp;&nbsp;return "" + item + index;`}
                ${html`<br>`}
                ${"}, (item, index) => {"}
                ${html`<br>`}
                ${html`&nbsp;&nbsp;&nbsp;&nbsp;return `}
                ${"html`<h1>${item}</h1>`"}
                ${");"}
                ${html`<br>`}
                ${"});"}
            </code-segment>
            <br>
            
            <h1 class='section-title' tabindex='0'>Array Caveats</h1>
            <p class='section-text' tabindex='0'>
                While the <b>Mosaic.list</b> function does provide a more efficient
                solution for rendering long lists of data, there is an important drawback
                that must be accounted for.
            </p>
            <p class='section-text' tabindex='0'>
                First, keys used in the Mosaic.list function <b>must</b> be globally unique.
                Based on the current renderer's algorithm, you cannot reuse keys between
                different lists. So when defining your keys, just make sure it is specific
                for every list you define.
            </p>
            <p class='section-text' tabindex='0'>
                Second, let's go back to how regular arrays are wrapped inside of a
                container div in order to be rendered. Well, we end up having to do the same
                thing for efficient arrays too, but only if they are being used inside of a
                conditional block (keep in mind, this goes for all adjacent elements that
                are conditionally rendered). So just keep in mind that if you are using a
                keyed array in a conditional render block, it must be wrapped inside of a
                single html tag.
            </p>
            <p class='section-text' tabindex='0'>
                Third, in order to conditionally render an efficient array in the first place
                we <u><i>must</i></u> use a function rather than using the ternary operator.
                Using a function to conditionally render an efficient array is currently the only
                way to render it conditionally. With the current implementation, failing to use a 
                function in this scenario will result in an array that only updates when the 
                condition is true.
            </p>
            <p class='section-text' tabindex='0'>
                The fourth point deals with using static and dynamic descendants together under
                a component. Normally, you can have both static and dynamic content inside of
                any HTML tag, however, when rendering arrays this is not currently possible. A
                workaround for this is to use just one dynamic child that renders another "html"
                function as opposed to using both static and dynamic content directly inside of
                the array renderer.
            </p>
            <br><br>

            <p class='section-text' tabindex='0' style='text-align:center;'>
                That's all there is to know about views in Mosaic! Next we will
                go over Mixins and how they can be used to reusable component
                functionality.
            </p>
            <div style="text-align: center;">
                <round-button onclick='${() => this.router.send('/actions')}'>
                    <ion-icon name="arrow-round-back"></ion-icon>
                    Actions and UDPs
                </round-button>
                &nbsp;&nbsp;
                <round-button onclick='${() => this.router.send('/mixins')}'>
                    Mixins
                    <ion-icon name="arrow-round-forward"></ion-icon>
                </round-button>
            </div>

            <app-footer></app-footer>
        </h1>`
    }
});